import { InfoCircleOutlined } from '@ant-design/icons';
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { observe } from 'mobx';
import styled from 'styled-components';
import moment from 'moment-timezone';
import _ from 'lodash';

import {
  DatePicker,
  Form,
  Input,
  Radio,
  AutoComplete,
  InputNumber,
  Row,
  Col,
  Button,
} from 'antd';

import { partList } from './data/general';
import { webServiceProvider } from 'shared';
import { convertDistanceValueToKMorMiles } from 'helpers/unitCalculations';
import { AppStore, ShopStore, CurrentUserStore, CarStore } from 'stores';
import { SelectCars } from 'containers/Users';

const RoutineContainer = styled.div`
  & > div {
    margin: 0px;
  }
  margin-bottom: 24px;

  .routineBased {
    font-weight: 500;
  }

  .ant-row {
    margin: 0px;
  }
`;

const RoutineServiceForm = () => {
  const [form] = Form.useForm();
  const [state, setState] = React.useState({
    selectedCarIds: [],
    carInfo: undefined,
    action: null,
    partName: null,
    description: null,
    priority: null,
    isTimeAlertOn: false,
    isTimeIntervalAlertOn: true,
    isMileageAlertOn: false,
    isEngineHoursAlertOn: false,
    isEngineHoursIntervalAlertOn: true,
    currentUnit: CurrentUserStore.user.displayedOdometer,
    timeAlert: {
      started: moment().format('YYYY-MM-DD'),
      interval: 1,
      threshold: 1,
    },
    mileageAlert: { started: 1, interval: 1, threshold: 1 },
    engineHoursAlert: { started: 1, interval: 1, threshold: 1 },
  });

  useEffect(() => {
    const disposer = observe(ShopStore.currentShop, 'id', () => {
      setState((prev) => ({
        ...prev,
        selectedCarIds: [],
      }));
    });

    return () => disposer();
  }, []);

  const onSelect = async (ids) => {
    const carInfo = await CarStore.data.get(ids[0]);
    setState((prev) => ({
      ...prev,
      selectedCarIds: ids,
      carInfo: carInfo,
    }));

    if (state.isMileageAlertOn) {
      form.setFieldValue(
        'started_mileage',
        Math.ceil(
          convertDistanceValueToKMorMiles(
            Number(carInfo?.totalMileage),
            'km',
            state.currentUnit
          )
        ) || 1
      );
    }

    if (state.isEngineHoursAlertOn) {
      form.setFieldValue(
        'started_engine_hours',
        Math.ceil(carInfo?.engineHours / 3600) || 1
      );
    }
  };
  const onSubmit = async () => {
    try {
      if (ShopStore.currentShop.id === -1) {
        throw Error('Please select another shop!');
      }

      const values = await form.validateFields();

      if (_.isEmpty(state.selectedCarIds)) {
        throw Error('Please select vehicles!');
      }

      if (
        !state.isTimeAlertOn &&
        !state.isMileageAlertOn &&
        !state.isEngineHoursAlertOn
      ) {
        throw Error('Please Select At Least One Alert!');
      }

      AppStore.addLoading('Service is being created...');
      await onAddService(values);
      AppStore.addSuccess('Service has been created!');
    } catch (err) {
      AppStore.addError(`Error in adding routine service: ${err.message}`);
    } finally {
      AppStore.removeLoading('Service is being created...');
    }
  };

  const onAddService = async (formValues) => {
    let rule_type = state.isTimeAlertOn
      ? 'time'
      : state.isMileageAlertOn
        ? 'mileage'
        : state.isEngineHoursAlertOn
          ? 'engine_hours'
          : undefined;

    const started_time =
      moment(formValues.started_time).unix() ||
      moment(state.timeAlert.started).unix();
    const started_mileage =
      formValues.started_mileage || state.mileageAlert.started;
    const started_engine_hours =
      formValues.started_engine_hours || state.engineHoursAlert.started;

    let actualCarInfo = [];
    for (const carId of state.selectedCarIds) {
      const carInfo = await CarStore.data.get(carId);
      if (rule_type === 'time') {
        actualCarInfo.push({ carId, time: new Date() });
      } else if (rule_type === 'mileage') {
        actualCarInfo.push({ carId, mileage: Number(carInfo.totalMileage) });
      } else if (rule_type === 'engine_hours') {
        actualCarInfo.push({ carId, engineHours: carInfo.engineHours });
      } else {
        actualCarInfo.push({
          carId,
          mileage: Number(carInfo.totalMileage),
          engineHours: carInfo.engineHours,
        });
      }
    }

    // Convert months to seconds
    let interval_time = 0;
    if (state.isTimeAlertOn && state.timeAlert.interval) {
      const duration = moment.duration(
        Number(state.timeAlert.interval),
        'months'
      );
      interval_time = duration.asSeconds();
    }

    const data = {
      shopId: ShopStore.currentShop.id,
      data: {
        action: state.action,
        item: state.partName,
        description: state.description || 'No description available',
        priority: state.priority,
        status: 'new',
        actualCarInfo,
        rule_type,
        interval_time:
          state.isTimeAlertOn && state.timeAlert.interval
            ? Number(interval_time)
            : null,
        started_time: state.isTimeAlertOn ? started_time : null,
        interval_mileage:
          state.isMileageAlertOn && Number(state.mileageAlert.interval)
            ? Math.ceil(
                convertDistanceValueToKMorMiles(
                  Number(state.mileageAlert.interval),
                  state.currentUnit,
                  'km'
                )
              )
            : null,
        started_mileage: state.isMileageAlertOn
          ? Math.ceil(
              convertDistanceValueToKMorMiles(
                Number(started_mileage),
                state.currentUnit,
                'km'
              )
            )
          : null,
        interval_engine_hours:
          state.isEngineHoursAlertOn && state.engineHoursAlert.interval
            ? Number(state.engineHoursAlert.interval) * 3600 // In seconds
            : null,
        started_engine_hours: state.isEngineHoursAlertOn
          ? Number(started_engine_hours) * 3600 // In seconds
          : null,
        threshold: {
          time:
            state.isTimeAlertOn && state.timeAlert.threshold
              ? state.timeAlert.threshold
              : null,
          mileage:
            state.isMileageAlertOn && state.mileageAlert.threshold
              ? Math.ceil(
                  convertDistanceValueToKMorMiles(
                    Number(state.mileageAlert.threshold),
                    state.currentUnit,
                    'km'
                  )
                )
              : null,
          engineHours:
            state.isEngineHoursAlertOn && state.engineHoursAlert.threshold
              ? state.engineHoursAlert.threshold * 3600
              : null,
        },
      },
      carIds: state.selectedCarIds,
    };

    // route that create the routine
    await webServiceProvider.post('v1/car/newPm', data);
  };

  const getTimeAlertText = () => {
    if (!state.isTimeAlertOn) return '';

    if (!_.isNil(state.timeAlert.interval)) {
      return (
        <p>
          This service will be created every{' '}
          <b>(Z) {`${state.timeAlert.interval}`} months</b> start from{' '}
          <b>(X) {`${state.timeAlert.started}`}</b> and alert{' '}
          <b>(Y) {`${state.timeAlert.threshold}`} days</b> before
        </p>
      );
    }
  };

  const getMileageAlertText = () => {
    if (!state.isMileageAlertOn) return '';

    if (!_.isNil(state.mileageAlert.interval)) {
      return (
        <p>
          This service will be created every{' '}
          <b>
            (Y){' '}
            {`${state.mileageAlert.interval} ${CurrentUserStore.user.displayedOdometer}`}
          </b>{' '}
          and alert{' '}
          <b>
            (Z){' '}
            {`${state.mileageAlert.threshold} ${CurrentUserStore.user.displayedOdometer}`}
          </b>{' '}
          before
        </p>
      );
    }
  };

  const getEngineHoursAlertText = () => {
    if (!state.isEngineHoursAlertOn) return '';

    if (!_.isNil(state.engineHoursAlert.interval)) {
      return (
        <p>
          This service will be created every{' '}
          <b>(Y) {`${state.engineHoursAlert.interval}`} engine hours</b> and
          alert <b>(z) {`${state.engineHoursAlert.threshold}`} engine hours</b>{' '}
          before
        </p>
      );
    }
  };

  return (
    <>
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Form
            form={form}
            layout="vertical"
            name="routine-service"
            initialValues={{
              interval_time: 1,
              thresholdTime: 1,
              interval_mileage: 1,
              thresholdMiles: 1,
              interval_engine_hours: 1,
              thresholdEngineHours: 1,
            }}
          >
            <Form.Item
              label="Action / Service Name"
              name="action"
              rules={[
                {
                  required: true,
                  message: 'Please input a Service name!',
                },
              ]}
            >
              <AutoComplete
                options={[
                  'Change',
                  'Inspect',
                  'Perform',
                  'Replace',
                  'Remind',
                  'Rotate',
                ].map((value) => ({ value }))}
                filterOption={(inputValue, option) =>
                  option?.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                onChange={(val) =>
                  setState((prev) => ({
                    ...prev,
                    action: val,
                  }))
                }
              />
            </Form.Item>

            <Form.Item
              label="Part Name"
              name="partName"
              rules={[
                {
                  required: true,
                  message: 'Please input a Part Name!',
                },
              ]}
            >
              <AutoComplete
                options={partList.map((value) => ({ value }))}
                filterOption={(inputValue, option) =>
                  option?.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                onChange={(val) =>
                  setState((prev) => ({
                    ...prev,
                    partName: val,
                  }))
                }
              />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input.TextArea
                autoSize={{ minRows: 3, maxRows: 5 }}
                onChange={(e) => {
                  setState((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }));
                }}
              />
            </Form.Item>

            <Form.Item
              label="Priority"
              name="priority"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Radio.Group
                size="large"
                onChange={(e) => {
                  let priority;
                  switch (e.target.value) {
                    case 'Minor':
                      priority = 1;
                      break;
                    case 'Major':
                      priority = 3;
                      break;
                    case 'Critical':
                      priority = 4;
                      break;
                    default:
                      priority = 4;
                  }
                  setState((prev) => ({
                    ...prev,
                    priority,
                  }));
                }}
              >
                <Radio value="Minor">
                  <p
                    style={{
                      backgroundColor: '#dec431',
                      color: 'white',
                      display: 'inline',
                      padding: '5px',
                      borderRadius: '3px',
                    }}
                  >
                    Minor
                  </p>
                </Radio>
                <Radio value="Major">
                  <p
                    style={{
                      backgroundColor: '#e28931',
                      color: 'white',
                      display: 'inline',
                      padding: '5px',
                      borderRadius: '3px',
                    }}
                  >
                    Major
                  </p>
                </Radio>
                <Radio value="Critical">
                  <p
                    style={{
                      backgroundColor: '#f92d2e',
                      color: 'white',
                      display: 'inline',
                      padding: '5px',
                      borderRadius: '3px',
                    }}
                  >
                    Critical
                  </p>
                </Radio>
              </Radio.Group>
            </Form.Item>

            <RoutineContainer>
              <Form.Item className="routineBased" label="Set Time Alert ?">
                <Radio.Group
                  buttonStyle="solid"
                  size="small"
                  name="timeBased"
                  value={state.isTimeAlertOn ? 'yes' : 'no'}
                  onChange={() =>
                    setState((prev) => ({
                      ...prev,
                      isTimeAlertOn: !prev.isTimeAlertOn,
                      isTimeIntervalAlertOn: false,
                      isMileageAlertOn: false,
                      isEngineHoursAlertOn: false,
                    }))
                  }
                >
                  <Radio.Button value="no">No</Radio.Button>
                  <Radio.Button value="yes">Yes</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {state.isTimeAlertOn && (
                <>
                  <Row>
                    <Col span={6}>
                      <Form.Item
                        label="Service Start On (X)?"
                        name="started_time"
                        initialValue={moment()}
                        rules={[
                          { required: true, message: 'This is required' },
                        ]}
                      >
                        <DatePicker
                          onChange={(date) =>
                            setState((prev) => ({
                              ...prev,
                              timeAlert: {
                                ...prev.timeAlert,
                                started: moment(date).format('YYYY-MM-DD'),
                              },
                            }))
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label="Service Repeat Every (Y) Months?"
                        name="interval_time"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex' }}>
                          <InputNumber
                            min={0}
                            onChange={(val) => {
                              setState((prev) => ({
                                ...prev,
                                timeAlert: {
                                  ...prev.timeAlert,
                                  interval: val,
                                },
                              }));
                            }}
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            Months
                          </p>
                        </div>
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        label="Alert Service Before (Z) Days?"
                        name="thresholdTime"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex' }}>
                          <InputNumber
                            min={0}
                            onChange={(val) =>
                              setState((prev) => ({
                                ...prev,
                                timeAlert: {
                                  ...prev.timeAlert,
                                  threshold: val,
                                },
                              }))
                            }
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            days
                          </p>
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24} style={{ display: 'flex' }}>
                      <InfoCircleOutlined
                        style={{
                          fontSize: 14,
                          marginRight: 8,
                          marginTop: 4,
                        }}
                      />
                      <i>{getTimeAlertText()}</i>
                    </Col>
                  </Row>
                </>
              )}
            </RoutineContainer>

            <RoutineContainer>
              <Form.Item className="routineBased" label="Set Mileage Alert?">
                <Radio.Group
                  buttonStyle="solid"
                  size="small"
                  name="mileageBased"
                  value={state.isMileageAlertOn ? 'yes' : 'no'}
                  onChange={() =>
                    setState((prev) => ({
                      ...prev,
                      isMileageAlertOn: !prev.isMileageAlertOn,
                      isTimeAlertOn: false,
                      isEngineHoursAlertOn: false,
                    }))
                  }
                >
                  <Radio.Button value="no">No</Radio.Button>
                  <Radio.Button value="yes">Yes</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {state.isMileageAlertOn && (
                <>
                  <Row>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <span>
                            Service Start at (X) Mileage?
                            <br />
                            <span
                              style={{ fontWeight: 'normal', color: '#888' }}
                            >
                              (Current Mileage as Default)
                            </span>
                          </span>
                        }
                        name="started_mileage"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex' }}>
                          <InputNumber
                            min={0}
                            onChange={(val) => {
                              setState((prev) => ({
                                ...prev,
                                mileageAlert: {
                                  ...prev.mileageAlert,
                                  started: val,
                                },
                              }));
                            }}
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            {CurrentUserStore.user.displayedOdometer}
                          </p>
                        </div>
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        label="Service Repeat Every (Y) Mileage?"
                        name="interval_mileage"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex', marginTop: '21px' }}>
                          <InputNumber
                            min={0}
                            onChange={(val) => {
                              setState((prev) => ({
                                ...prev,
                                mileageAlert: {
                                  ...prev.mileageAlert,
                                  interval: val,
                                },
                              }));
                            }}
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            {CurrentUserStore.user.displayedOdometer}
                          </p>
                        </div>
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        label="Alert Service Before (Z) Mileage?"
                        name="thresholdMiles"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex', marginTop: '21px' }}>
                          <InputNumber
                            min={0}
                            onChange={(val) =>
                              setState((prev) => ({
                                ...prev,
                                mileageAlert: {
                                  ...prev.mileageAlert,
                                  threshold: val,
                                },
                              }))
                            }
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            {CurrentUserStore.user.displayedOdometer}
                          </p>
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24} style={{ display: 'flex' }}>
                      <InfoCircleOutlined
                        style={{
                          fontSize: 14,
                          marginRight: 8,
                          marginTop: 4,
                        }}
                      />
                      <i>{getMileageAlertText()}</i>
                    </Col>
                  </Row>
                </>
              )}
            </RoutineContainer>

            <RoutineContainer>
              <Form.Item
                className="routineBased"
                label="Set Engine Hours Alert?"
              >
                <Radio.Group
                  buttonStyle="solid"
                  size="small"
                  name="engineHoursBased"
                  value={state.isEngineHoursAlertOn ? 'yes' : 'no'}
                  onChange={() =>
                    setState((prev) => ({
                      ...prev,
                      isEngineHoursAlertOn: !prev.isEngineHoursAlertOn,
                      isEngineHoursIntervalAlertOn: false,
                      isMileageAlertOn: false,
                      isTimeAlertOn: false,
                    }))
                  }
                >
                  <Radio.Button value="no">No</Radio.Button>
                  <Radio.Button value="yes">Yes</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {state.isEngineHoursAlertOn && (
                <>
                  <Row>
                    <Col span={6}>
                      <Form.Item
                        label={
                          <span>
                            Service Start At (X) Engine Hours?
                            <br />
                            <span
                              style={{ fontWeight: 'normal', color: '#888' }}
                            >
                              (Current Engine Hours as Default)
                            </span>
                          </span>
                        }
                        name="started_engine_hours"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex' }}>
                          <InputNumber
                            min={1}
                            onChange={(val) => {
                              setState((prev) => ({
                                ...prev,
                                engineHoursAlert: {
                                  ...prev.engineHoursAlert,
                                  started: val,
                                },
                              }));
                            }}
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            hours
                          </p>
                        </div>
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        label="Service Repeat Every (Y) Engine Hours?"
                        name="interval_engine_hours"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex', marginTop: '21px' }}>
                          <InputNumber
                            min={1}
                            onChange={(val) => {
                              setState((prev) => ({
                                ...prev,
                                engineHoursAlert: {
                                  ...prev.engineHoursAlert,
                                  interval: val,
                                },
                              }));
                            }}
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            hours
                          </p>
                        </div>
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        label="Alert Service Before (Z) Engine Hours?"
                        name="thresholdEngineHours"
                        rules={[
                          {
                            required: true,
                            type: 'number',
                            message: 'This is required and must be a number!',
                          },
                        ]}
                      >
                        <div style={{ display: 'flex', marginTop: '21px' }}>
                          <InputNumber
                            min={1}
                            onChange={(val) =>
                              setState((prev) => ({
                                ...prev,
                                engineHoursAlert: {
                                  ...prev.engineHoursAlert,
                                  threshold: val,
                                },
                              }))
                            }
                          />
                          <p
                            style={{
                              marginLeft: 10,
                              marginTop: 5,
                              fontWeight: 500,
                            }}
                          >
                            hours
                          </p>
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24} style={{ display: 'flex' }}>
                      <InfoCircleOutlined
                        style={{
                          fontSize: 14,
                          marginRight: 8,
                          marginTop: 4,
                        }}
                      />
                      <i>{getEngineHoursAlertText()}</i>
                    </Col>
                  </Row>
                </>
              )}
            </RoutineContainer>
          </Form>
        </Col>

        <Col span={24}>
          <b>
            <p>Select Vehicle</p>
          </b>
          <SelectCars
            rowSelection={{
              selectedCarIds: state.selectedCarIds,
              onChange: onSelect,
              type: 'radio',
            }}
          />

          <Button
            type="primary"
            onClick={onSubmit}
            style={{ marginTop: '10px' }}
          >
            Add Service
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default observer(RoutineServiceForm);
